import {
  Timestamp,
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { getFirestore } from "firebase/firestore";

const db = getFirestore();

export const KEYCHAIN_CAMPAIGN = "keychain";
export const KEYCHAIN_ATTRIBUTION_TTL_MS = 30 * 24 * 60 * 60 * 1000;

const KEYCHAIN_VISITS_PATH = "analytics/keychainCampaign/visits";
const ATTRIBUTION_STORAGE_KEY = "mytassel:keychainAttribution:v1";
const VISITOR_STORAGE_KEY = "mytassel:keychainVisitorId:v1";
const ADMIN_EMAIL_WHITELIST = new Set([
  "trevorhawkins@dsignable.com",
  "info@mytassel.com",
]);

const removeUndefined = (value) => (
  Object.fromEntries(Object.entries(value).filter(([, entryValue]) => entryValue !== undefined))
);

const getStorage = () => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const createId = (prefix) => {
  const cryptoObject = typeof crypto !== "undefined" ? crypto : null;
  const randomPart = cryptoObject?.randomUUID
    ? cryptoObject.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
  return `${prefix}_${randomPart}`;
};

const getOrCreateVisitorId = () => {
  const storage = getStorage();
  if (!storage) return createId("visitor");

  const existing = storage.getItem(VISITOR_STORAGE_KEY);
  if (existing) return existing;

  const visitorId = createId("visitor");
  storage.setItem(VISITOR_STORAGE_KEY, visitorId);
  return visitorId;
};

export const isInternalKeychainAccount = (userData = {}) => {
  const email = String(userData?.email || "").toLowerCase();
  return Boolean(
    userData?.isTester ||
    userData?.accountType === "admin" ||
    ADMIN_EMAIL_WHITELIST.has(email)
  );
};

export const isKeychainAttributionFresh = (attribution, nowMs = Date.now()) => (
  attribution?.campaign === KEYCHAIN_CAMPAIGN &&
  Number(attribution?.expiresAtMs || 0) > nowMs &&
  Boolean(attribution?.visitId)
);

const readAttribution = () => {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const parsed = JSON.parse(storage.getItem(ATTRIBUTION_STORAGE_KEY) || "null");
    if (!isKeychainAttributionFresh(parsed)) {
      storage.removeItem(ATTRIBUTION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    storage.removeItem(ATTRIBUTION_STORAGE_KEY);
    return null;
  }
};

const writeAttribution = (attribution) => {
  const storage = getStorage();
  if (!storage) return;
  storage.setItem(ATTRIBUTION_STORAGE_KEY, JSON.stringify(attribution));
};

const clearAttribution = () => {
  const storage = getStorage();
  if (storage) storage.removeItem(ATTRIBUTION_STORAGE_KEY);
};

const visitDocRef = (visitId) => doc(db, KEYCHAIN_VISITS_PATH, visitId);

const asMillis = (value) => {
  if (!value) return 0;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.toDate === "function") return value.toDate().getTime();
  const parsed = new Date(value).getTime();
  return Number.isFinite(parsed) ? parsed : 0;
};

export const getPendingKeychainRegistrationFields = () => {
  const attribution = readAttribution();
  if (!attribution) return null;

  return {
    sourceCampaign: KEYCHAIN_CAMPAIGN,
    keychainAttribution: {
      visitId: attribution.visitId,
      visitorId: attribution.visitorId,
      arrivedAt: attribution.arrivedAtIso,
      expiresAt: attribution.expiresAtIso,
    },
  };
};

export const recordKeychainVisit = async ({ authUser = null, userData = null } = {}) => {
  try {
    if (userData && isInternalKeychainAccount(userData)) {
      clearAttribution();
      return { skipped: true, reason: "internal_account" };
    }

    const nowMs = Date.now();
    const expiresAtMs = nowMs + KEYCHAIN_ATTRIBUTION_TTL_MS;
    const visitId = createId("keychainVisit");
    const visitorId = getOrCreateVisitorId();
    const isAuthenticated = Boolean(authUser && !authUser.isAnonymous);
    const sourceUrl = typeof window !== "undefined" ? window.location.href : "";

    const visitData = removeUndefined({
      campaign: KEYCHAIN_CAMPAIGN,
      visitId,
      visitorId,
      arrivedAt: Timestamp.fromMillis(nowMs),
      arrivedAtMs: nowMs,
      expiresAt: Timestamp.fromMillis(expiresAtMs),
      expiresAtMs,
      sourcePath: typeof window !== "undefined" ? window.location.pathname : "/keychain",
      sourceUrl,
      referrer: typeof document !== "undefined" ? document.referrer || "direct" : "direct",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "unknown",
      authState: isAuthenticated ? "authenticated" : "anonymous",
      wasAuthenticated: isAuthenticated,
      userId: authUser?.uid || null,
      userEmail: userData?.email || authUser?.email || null,
      userCustomUID: userData?.uid || null,
      accountType: userData?.accountType || null,
      converted: false,
      existingAccountSignIn: false,
    });

    await setDoc(visitDocRef(visitId), visitData);

    if (isAuthenticated) {
      clearAttribution();
    } else {
      writeAttribution({
        campaign: KEYCHAIN_CAMPAIGN,
        visitId,
        visitorId,
        arrivedAtMs: nowMs,
        arrivedAtIso: new Date(nowMs).toISOString(),
        expiresAtMs,
        expiresAtIso: new Date(expiresAtMs).toISOString(),
        sourceUrl,
      });
    }

    return { skipped: false, visitId, visitorId };
  } catch (error) {
    return { skipped: true, reason: error.message };
  }
};

export const recordKeychainExistingSignIn = async (authUser, userData = {}) => {
  try {
    const attribution = readAttribution();
    if (!attribution) return { skipped: true, reason: "no_attribution" };

    if (isInternalKeychainAccount(userData)) {
      try {
        await updateDoc(visitDocRef(attribution.visitId), removeUndefined({
          internalExcluded: true,
          excludedAt: Timestamp.now(),
          excludedReason: "internal_existing_account_sign_in",
          signedInUserId: authUser?.uid || null,
          signedInUserEmail: userData?.email || authUser?.email || null,
          signedInUserCustomUID: userData?.uid || null,
        }));
      } catch {}
      clearAttribution();
      return { skipped: true, reason: "internal_account" };
    }

    await updateDoc(visitDocRef(attribution.visitId), removeUndefined({
      existingAccountSignIn: true,
      signedInAt: Timestamp.now(),
      signedInUserId: authUser?.uid || null,
      signedInUserEmail: userData?.email || authUser?.email || null,
      signedInUserCustomUID: userData?.uid || null,
      outcome: "existing_account_sign_in",
    }));

    clearAttribution();
    return { skipped: false, visitId: attribution.visitId };
  } catch (error) {
    return { skipped: true, reason: error.message };
  }
};

export const recordKeychainRegistrationConversion = async (authUser, userData = {}) => {
  try {
    const attribution = readAttribution();
    if (!attribution) return { skipped: true, reason: "no_attribution" };

    if (isInternalKeychainAccount(userData)) {
      try {
        await updateDoc(visitDocRef(attribution.visitId), removeUndefined({
          internalExcluded: true,
          excludedAt: Timestamp.now(),
          excludedReason: "internal_registration",
          registeredUserId: authUser?.uid || null,
          registeredUserEmail: userData?.email || authUser?.email || null,
          registeredUserCustomUID: userData?.uid || null,
        }));
      } catch {}
      clearAttribution();
      return { skipped: true, reason: "internal_account" };
    }

    await updateDoc(visitDocRef(attribution.visitId), removeUndefined({
      converted: true,
      registeredAt: Timestamp.now(),
      registeredUserId: authUser?.uid || null,
      registeredUserEmail: userData?.email || authUser?.email || null,
      registeredUserCustomUID: userData?.uid || null,
      outcome: "registration",
    }));

    clearAttribution();
    return { skipped: false, visitId: attribution.visitId };
  } catch (error) {
    return { skipped: true, reason: error.message };
  }
};

export const getKeychainCampaignMetrics = async (startDate, endDate) => {
  try {
    const visitsQuery = query(
      collection(db, KEYCHAIN_VISITS_PATH),
      where("arrivedAt", ">=", Timestamp.fromDate(startDate)),
      where("arrivedAt", "<=", Timestamp.fromDate(endDate))
    );

    const snapshot = await getDocs(visitsQuery);
    const uniqueVisitors = new Set();
    const recentVisits = [];
    const metrics = {
      visits: 0,
      uniqueVisitors: 0,
      anonymousVisits: 0,
      authenticatedArrivals: 0,
      existingAccountSignIns: 0,
      registrations: 0,
      conversionRate: 0,
      recentVisits,
    };

    snapshot.forEach((visitDoc) => {
      const data = visitDoc.data();
      if (data.internalExcluded === true) return;

      metrics.visits += 1;
      if (data.visitorId) uniqueVisitors.add(data.visitorId);
      if (data.wasAuthenticated || data.authState === "authenticated") metrics.authenticatedArrivals += 1;
      if (!data.wasAuthenticated && data.authState !== "authenticated") metrics.anonymousVisits += 1;
      if (data.existingAccountSignIn) metrics.existingAccountSignIns += 1;
      if (data.converted) metrics.registrations += 1;

      recentVisits.push({
        id: visitDoc.id,
        arrivedAtMs: data.arrivedAtMs || asMillis(data.arrivedAt),
        authState: data.authState || (data.wasAuthenticated ? "authenticated" : "anonymous"),
        outcome: data.outcome || (data.converted ? "registration" : data.existingAccountSignIn ? "existing_account_sign_in" : "visit"),
      });
    });

    metrics.uniqueVisitors = uniqueVisitors.size;
    metrics.existingAccountTraffic = metrics.authenticatedArrivals + metrics.existingAccountSignIns;
    metrics.conversionRate = metrics.anonymousVisits > 0
      ? (metrics.registrations / metrics.anonymousVisits) * 100
      : 0;
    metrics.recentVisits = recentVisits
      .sort((a, b) => b.arrivedAtMs - a.arrivedAtMs);

    return metrics;
  } catch (error) {
    return {
      visits: 0,
      uniqueVisitors: 0,
      anonymousVisits: 0,
      authenticatedArrivals: 0,
      existingAccountSignIns: 0,
      existingAccountTraffic: 0,
      registrations: 0,
      conversionRate: 0,
      recentVisits: [],
      error: error.message,
    };
  }
};
